import React, {useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap'
import './Configuration.scss';


interface ConfigurationProps {
  show: boolean;
  handleClose: () => void;
  prefs?: { showNumber: boolean; showTag: boolean; showElapsed: boolean; showCharges: boolean; showWaiter: boolean; showPeopleType: 'capacity' | 'current' };
  onSave?: (next: { showNumber: boolean; showTag: boolean; showElapsed: boolean; showCharges: boolean; showWaiter: boolean; showPeopleType: 'capacity' | 'current' }) => void;
}

const Configuration: React.FC<ConfigurationProps> = ({ show, handleClose, prefs, onSave }) => {
  const [local, setLocal] = useState({
    showNumber: true,
    showTag: false,
    showElapsed: true,
    showCharges: true,
    showWaiter: true,
    showPeopleType: 'capacity' as 'capacity' | 'current',
  });

  useEffect(() => {
    if (prefs) setLocal({ ...local, ...prefs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs, show]);

  const handleToggle = (key: keyof typeof local) => {
    setLocal(prev => {
      return { ...prev, [key]: !prev[key] };
    });
  };

  const handleSave = () => {
    // Enforce mutual exclusion: if both are enabled, keep only number
    const next = { ...local };
    if (next.showNumber && next.showTag) {
      next.showNumber = true;
      next.showTag = false;
    }
    // Ensure at least one is enabled
    if (!next.showNumber && !next.showTag) {
      next.showNumber = true;
    }
    onSave && onSave(next);
    handleClose();
  };

  const handleReset = () => {
    setLocal({ showNumber: true, showTag: false, showElapsed: true, showCharges: true, showWaiter: true, showPeopleType: 'capacity' });
  };

  return (
    <Modal show={show} onHide={handleClose} centered contentClassName="mp-config-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <h3 className="form-modal-title">Configuración</h3>
        </div>
        <button className="modal-close-button" onClick={handleClose} aria-label="Cerrar modal">×</button>
      </Modal.Header>
      <Modal.Body>
        <div className="cfg-section">
          <div className="cfg-section__title">Identificación de mesa</div>
          <div className="cfg-section__desc">Controla cómo se identifica cada mesa. Debes elegir una opción.</div>
          <div className="cfg-radio-group">
            <label className="cfg-radio-option">
              <input 
                type="radio" 
                name="mesaIdentification" 
                checked={local.showNumber && !local.showTag} 
                onChange={() => setLocal(prev => ({ ...prev, showNumber: true, showTag: false }))}
              />
              <span className="cfg-radio-label">
                <strong>Mostrar número de mesa</strong>
                <small>Identifica las mesas por su número asignado</small>
              </span>
            </label>
            <label className="cfg-radio-option">
              <input 
                type="radio" 
                name="mesaIdentification" 
                checked={local.showTag && !local.showNumber} 
                onChange={() => setLocal(prev => ({ ...prev, showNumber: false, showTag: true }))}
              />
              <span className="cfg-radio-label">
                <strong>Mostrar etiqueta de mesa</strong>
                <small>Identifica las mesas por su etiqueta personalizada</small>
              </span>
            </label>
          </div>
        </div>

        <div className="cfg-section">
          <div className="cfg-section__title">Estado y tiempo</div>
          <div className="cfg-section__desc">Muestra el tiempo transcurrido desde que la mesa se asignó. Útil para rotación y seguimiento.</div>
          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">Mostrar tiempo transcurrido</div>
            <label className="cfg-switch">
              <input type="checkbox" checked={local.showElapsed} onChange={() => handleToggle('showElapsed')} aria-checked={local.showElapsed} />
              <span className="slider"><span className="knob" /></span>
            </label>
          </div>
        </div>

        <div className="cfg-section">
          <div className="cfg-section__title">Ventas en mesa</div>
          <div className="cfg-section__desc">Muestra un badge con el monto cobrado en tiempo real para cada mesa.</div>
          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">Mostrar monto cobrado</div>
            <label className="cfg-switch">
              <input type="checkbox" checked={local.showCharges} onChange={() => handleToggle('showCharges')} aria-checked={local.showCharges} />
              <span className="slider"><span className="knob" /></span>
            </label>
          </div>
        </div>

        <div className="cfg-section">
          <div className="cfg-section__title">Atendido por</div>
          <div className="cfg-section__desc">Muestra el nombre o inicial del mesero asignado a la mesa.</div>
          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">Mostrar nombre/mesero</div>
            <label className="cfg-switch">
              <input type="checkbox" checked={local.showWaiter} onChange={() => handleToggle('showWaiter')} aria-checked={local.showWaiter} />
              <span className="slider"><span className="knob" /></span>
            </label>
          </div>
        </div>

        <div className="cfg-section">
          <div className="cfg-section__title">Ocupación de mesas</div>
          <div className="cfg-section__desc">Elige qué información mostrar sobre la ocupación de las mesas.</div>
          <div className="cfg-radio-group">
            <label className="cfg-radio-option">
              <input 
                type="radio" 
                name="peopleType" 
                checked={local.showPeopleType === 'capacity'} 
                onChange={() => setLocal(prev => ({ ...prev, showPeopleType: 'capacity' }))}
              />
              <span className="cfg-radio-label">
                <strong>Capacidad de mesa</strong>
                <small>Muestra la capacidad máxima de la mesa (siempre visible)</small>
              </span>
            </label>
            <label className="cfg-radio-option">
              <input 
                type="radio" 
                name="peopleType" 
                checked={local.showPeopleType === 'current'} 
                onChange={() => setLocal(prev => ({ ...prev, showPeopleType: 'current' }))}
              />
              <span className="cfg-radio-label">
                <strong>Personas actuales</strong>
                <small>Muestra la cantidad de personas en la mesa (solo si hay ocupación)</small>
              </span>
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button className="btn form-button-cancel" onClick={handleReset}>Restablecer</button>
          <button className="btn form-button-cancel" onClick={handleClose}>Cancelar</button>
          <button className="btn form-button-create" onClick={handleSave}>Guardar</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}   

export default Configuration;